import React, { useState, useEffect, useRef } from 'react';
import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signInWithPhoneNumber,
  RecaptchaVerifier,
} from 'firebase/auth';
import { getDatabase, ref, get } from 'firebase/database';
import { FirebaseConfig } from '../config/FirebaseConfig';

// ─── WhatsApp palette ────────────────────────────────────────────────────────
const W = {
  header:  '#075E54',
  primary: '#128C7E',
  green:   '#25D366',
  lightGreen: '#DCF8C6',
  bg:      '#F0F2F5',
  border:  '#E9EDEF',
  text:    '#111B21',
  muted:   '#667781',
  white:   '#FFFFFF',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
function getApp_() {
  return getApps().length ? getApp() : initializeApp(FirebaseConfig);
}

async function validateAdmin(uid) {
  const db = getDatabase(getApp_());
  const snap = await get(ref(db, `users/${uid}`));
  const u = snap.val();
  const ALLOWED = ['admin', 'fleetadmin', 'call_center_agent'];
  return u && ALLOWED.includes(u.usertype);
}

// ─── Styles ──────────────────────────────────────────────────────────────────
const S = {
  root: {
    minHeight: '100vh', background: W.bg, display: 'flex',
    flexDirection: 'column', fontFamily: 'Roboto, sans-serif',
  },
  header: {
    background: W.header, padding: '20px 24px',
    display: 'flex', alignItems: 'center', gap: 12,
    boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
  },
  headerLogo: {
    width: 40, height: 40, borderRadius: '50%',
    background: W.green, display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 22, fontWeight: 700, color: W.white,
  },
  headerTitle: { color: W.white, fontSize: 20, fontWeight: 600, margin: 0 },
  headerSub:   { color: 'rgba(255,255,255,0.75)', fontSize: 13, margin: 0 },
  body: {
    flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center',
    padding: 24,
  },
  card: {
    background: W.white, borderRadius: 12, width: '100%', maxWidth: 420,
    boxShadow: '0 2px 20px rgba(0,0,0,0.10)', overflow: 'hidden',
  },
  tabs: { display: 'flex', borderBottom: `2px solid ${W.border}` },
  tab: (active) => ({
    flex: 1, padding: '14px 8px', textAlign: 'center', cursor: 'pointer',
    fontSize: 13, fontWeight: active ? 600 : 400,
    color: active ? W.primary : W.muted,
    borderBottom: active ? `3px solid ${W.primary}` : '3px solid transparent',
    marginBottom: -2, transition: 'all 0.2s',
    userSelect: 'none',
  }),
  form: { padding: '28px 28px 24px' },
  label: { fontSize: 13, color: W.muted, marginBottom: 6, display: 'block' },
  input: {
    width: '100%', padding: '12px 14px', borderRadius: 8, fontSize: 15,
    border: `1.5px solid ${W.border}`, outline: 'none', color: W.text,
    background: W.bg, boxSizing: 'border-box', transition: 'border-color 0.2s',
  },
  inputFocus: { borderColor: W.primary },
  btn: (disabled) => ({
    width: '100%', padding: '13px', borderRadius: 24, border: 'none',
    background: disabled ? W.border : W.green,
    color: disabled ? W.muted : W.white,
    fontSize: 15, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
    marginTop: 18, transition: 'background 0.2s',
  }),
  googleBtn: {
    width: '100%', padding: '12px', borderRadius: 24,
    border: `1.5px solid ${W.border}`, background: W.white,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 10, cursor: 'pointer', fontSize: 15, fontWeight: 500,
    color: W.text, marginTop: 8, transition: 'background 0.2s',
  },
  error: {
    background: '#FFF3F3', border: '1px solid #FFCDD2', borderRadius: 8,
    padding: '10px 14px', color: '#C62828', fontSize: 13, marginTop: 14,
  },
  success: {
    background: W.lightGreen, border: `1px solid ${W.primary}`, borderRadius: 8,
    padding: '10px 14px', color: W.header, fontSize: 13, marginTop: 14,
  },
  divider: {
    display: 'flex', alignItems: 'center', gap: 10,
    color: W.muted, fontSize: 13, margin: '16px 0',
  },
  dividerLine: { flex: 1, height: 1, background: W.border },
  phoneRow: { display: 'flex', gap: 8 },
  countryInput: {
    width: 80, padding: '12px 8px', borderRadius: 8, fontSize: 15,
    border: `1.5px solid ${W.border}`, outline: 'none', color: W.text,
    background: W.bg, boxSizing: 'border-box',
  },
  spinner: {
    width: 18, height: 18, borderRadius: '50%',
    border: `3px solid ${W.border}`, borderTopColor: W.white,
    animation: 'spin 0.7s linear infinite', display: 'inline-block',
  },
};

// ─── Main component ───────────────────────────────────────────────────────────
export default function AdminLoginScreen({ onSuccess }) {
  const [loginMethods, setLoginMethods] = useState({ email: true, google: false, phone: false });
  const [tab, setTab] = useState('email'); // 'email' | 'google' | 'phone'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+222');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [confirmResult, setConfirmResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [focusedInput, setFocusedInput] = useState('');
  const recaptchaRef = useRef(null);
  const recaptchaVerifierRef = useRef(null);

  // Load enabled login methods from Firebase settings
  useEffect(() => {
    const app = getApp_();
    const db = getDatabase(app);
    get(ref(db, 'settings')).then(snap => {
      const s = snap.val() || {};
      const methods = {
        email: s.emailLogin !== false,
        google: !!s.socialLogin,
        phone: !!s.mobileLogin,
      };
      setLoginMethods(methods);
      // Set initial tab to first enabled method
      if (!methods.email && methods.google) setTab('google');
      else if (!methods.email && !methods.google && methods.phone) setTab('phone');
    }).catch(() => {});
  }, []);

  // Reset state on tab switch
  useEffect(() => {
    setError(''); setInfo(''); setOtpSent(false);
    setConfirmResult(null); setOtp('');
  }, [tab]);

  // Initialize invisible recaptcha for phone tab
  useEffect(() => {
    if (tab !== 'phone') return;
    const auth = getAuth(getApp_());
    setTimeout(() => {
      if (!recaptchaVerifierRef.current && recaptchaRef.current) {
        try {
          recaptchaVerifierRef.current = new RecaptchaVerifier(
            auth, recaptchaRef.current, { size: 'invisible' }
          );
        } catch { /* already set */ }
      }
    }, 300);
    return () => {
      if (recaptchaVerifierRef.current) {
        try { recaptchaVerifierRef.current.clear(); } catch { /* ignore */ }
        recaptchaVerifierRef.current = null;
      }
    };
  }, [tab]);

  const handleEmailLogin = async () => {
    if (!email || !password) { setError('Please enter email and password.'); return; }
    setLoading(true); setError('');
    try {
      const auth = getAuth(getApp_());
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const ok = await validateAdmin(cred.user.uid);
      if (ok) { onSuccess(); } else {
        await auth.signOut();
        setError('Access restricted. Please sign in.');
      }
    } catch (e) {
      setError(friendlyError(e.code));
    } finally { setLoading(false); }
  };

  const handleGoogleLogin = async () => {
    setLoading(true); setError('');
    try {
      const auth = getAuth(getApp_());
      const provider = new GoogleAuthProvider();
      const cred = await signInWithPopup(auth, provider);
      const ok = await validateAdmin(cred.user.uid);
      if (ok) { onSuccess(); } else {
        await auth.signOut();
        setError('Access restricted. Please sign in.');
      }
    } catch (e) {
      if (e.code !== 'auth/popup-closed-by-user') {
        setError(friendlyError(e.code));
      }
    } finally { setLoading(false); }
  };

  const handleSendOtp = async () => {
    const full = `${countryCode}${phone.replace(/^0/, '')}`;
    if (phone.length < 6) { setError('Enter a valid phone number.'); return; }
    setLoading(true); setError('');
    try {
      const auth = getAuth(getApp_());
      if (!recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current = new RecaptchaVerifier(
          auth, recaptchaRef.current, { size: 'invisible' }
        );
      }
      const result = await signInWithPhoneNumber(auth, full, recaptchaVerifierRef.current);
      setConfirmResult(result);
      setOtpSent(true);
      setInfo(`OTP sent to ${full}`);
    } catch (e) {
      setError(friendlyError(e.code));
    } finally { setLoading(false); }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length < 4) { setError('Enter the OTP code.'); return; }
    setLoading(true); setError('');
    try {
      const cred = await confirmResult.confirm(otp);
      const ok = await validateAdmin(cred.user.uid);
      if (ok) { onSuccess(); } else {
        const auth = getAuth(getApp_());
        await auth.signOut();
        setError('Access restricted. Please sign in.');
      }
    } catch (e) {
      setError(friendlyError(e.code));
    } finally { setLoading(false); }
  };

  const inputStyle = (name) => ({
    ...S.input,
    ...(focusedInput === name ? S.inputFocus : {}),
  });

  return (
    <div style={S.root}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Header */}
      <div style={S.header}>
        <div style={S.headerLogo}>S</div>
        <div>
          <p style={S.headerTitle}>Sawi Admin</p>
          <p style={S.headerSub}>Secure admin access</p>
        </div>
      </div>

      {/* Body */}
      <div style={S.body}>
        <div style={S.card}>
          {/* Tabs — only show methods enabled in app settings */}
          <div style={S.tabs}>
            {[
              loginMethods.email  && { id: 'email',  label: '✉️  Email' },
              loginMethods.google && { id: 'google', label: '🔵  Google' },
              loginMethods.phone  && { id: 'phone',  label: '📱  Phone' },
            ].filter(Boolean).map(({ id, label }) => (
              <div key={id} style={S.tab(tab === id)} onClick={() => setTab(id)}>
                {label}
              </div>
            ))}
          </div>

          {/* Email/Password */}
          {tab === 'email' && loginMethods.email && (
            <div style={S.form}>
              <label style={S.label}>Email address</label>
              <input
                type="email" value={email} placeholder="admin@example.com"
                onChange={e => setEmail(e.target.value)}
                onFocus={() => setFocusedInput('email')}
                onBlur={() => setFocusedInput('')}
                style={inputStyle('email')}
                onKeyDown={e => e.key === 'Enter' && handleEmailLogin()}
              />
              <div style={{ marginTop: 14 }}>
                <label style={S.label}>Password</label>
                <input
                  type="password" value={password} placeholder="••••••••"
                  onChange={e => setPassword(e.target.value)}
                  onFocus={() => setFocusedInput('pass')}
                  onBlur={() => setFocusedInput('')}
                  style={inputStyle('pass')}
                  onKeyDown={e => e.key === 'Enter' && handleEmailLogin()}
                />
              </div>
              {error && <div style={S.error}>{error}</div>}
              <button
                onClick={handleEmailLogin}
                disabled={loading || !email || !password}
                style={S.btn(loading || !email || !password)}
              >
                {loading ? <span style={S.spinner} /> : 'Sign In'}
              </button>
            </div>
          )}

          {/* Google */}
          {tab === 'google' && loginMethods.google && (
            <div style={S.form}>
              <p style={{ color: W.muted, fontSize: 14, textAlign: 'center', margin: '0 0 20px' }}>
                Sign in with your Google admin account to continue.
              </p>
              {error && <div style={S.error}>{error}</div>}
              <button onClick={handleGoogleLogin} disabled={loading} style={S.googleBtn}>
                {loading ? (
                  <span style={{ ...S.spinner, borderTopColor: W.primary }} />
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 48 48">
                      <path fill="#EA4335" d="M24 9.5c3.1 0 5.8 1.1 8 2.9l6-6C34.5 3.1 29.6 1 24 1 14.8 1 7 6.7 3.7 14.6l7 5.4C12.4 13.6 17.7 9.5 24 9.5z"/>
                      <path fill="#4285F4" d="M46.1 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h12.4c-.5 2.8-2.1 5.1-4.5 6.7l7 5.4c4.1-3.8 6.5-9.4 6.5-16.1z"/>
                      <path fill="#FBBC05" d="M10.7 28.6A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.1.7-4.6l-7-5.4A23 23 0 0 0 1 24c0 3.7.9 7.2 2.5 10.3l7.2-5.7z"/>
                      <path fill="#34A853" d="M24 47c5.6 0 10.3-1.8 13.7-5l-7-5.4C28.9 38 26.6 38.5 24 38.5c-6.3 0-11.6-4.2-13.5-9.9l-7.2 5.7C7 42.2 14.9 47 24 47z"/>
                    </svg>
                    Continue with Google
                  </>
                )}
              </button>
            </div>
          )}

          {/* Phone + OTP */}
          {tab === 'phone' && loginMethods.phone && (
            <div style={S.form}>
              {!otpSent ? (
                <>
                  <label style={S.label}>Phone number</label>
                  <div style={S.phoneRow}>
                    <input
                      type="text" value={countryCode}
                      onChange={e => setCountryCode(e.target.value)}
                      style={{ ...S.countryInput }}
                      placeholder="+222"
                    />
                    <input
                      type="tel" value={phone} placeholder="20000000"
                      onChange={e => setPhone(e.target.value)}
                      onFocus={() => setFocusedInput('phone')}
                      onBlur={() => setFocusedInput('')}
                      style={{ ...inputStyle('phone'), flex: 1 }}
                      onKeyDown={e => e.key === 'Enter' && handleSendOtp()}
                    />
                  </div>
                  {error && <div style={S.error}>{error}</div>}
                  <button
                    onClick={handleSendOtp}
                    disabled={loading || phone.length < 6}
                    style={S.btn(loading || phone.length < 6)}
                  >
                    {loading ? <span style={S.spinner} /> : 'Send OTP'}
                  </button>
                </>
              ) : (
                <>
                  {info && <div style={S.success}>{info}</div>}
                  <div style={{ marginTop: 14 }}>
                    <label style={S.label}>Enter OTP</label>
                    <input
                      type="text" value={otp} placeholder="------"
                      onChange={e => setOtp(e.target.value)}
                      onFocus={() => setFocusedInput('otp')}
                      onBlur={() => setFocusedInput('')}
                      style={{ ...inputStyle('otp'), letterSpacing: 6, textAlign: 'center', fontSize: 22 }}
                      maxLength={6}
                      onKeyDown={e => e.key === 'Enter' && handleVerifyOtp()}
                    />
                  </div>
                  {error && <div style={S.error}>{error}</div>}
                  <button
                    onClick={handleVerifyOtp}
                    disabled={loading || otp.length < 4}
                    style={S.btn(loading || otp.length < 4)}
                  >
                    {loading ? <span style={S.spinner} /> : 'Verify & Sign In'}
                  </button>
                  <button
                    onClick={() => { setOtpSent(false); setOtp(''); setError(''); setInfo(''); }}
                    style={{ ...S.btn(false), background: 'transparent', color: W.primary, marginTop: 8 }}
                  >
                    ← Back
                  </button>
                </>
              )}
              {/* invisible recaptcha anchor */}
              <div ref={recaptchaRef} id="recaptcha-admin-gate" />
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div style={{ textAlign: 'center', padding: '16px', color: W.muted, fontSize: 12 }}>
        Protected by Sawi Admin Gate · Unauthorized access is prohibited
      </div>
    </div>
  );
}

function friendlyError(code) {
  switch (code) {
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Incorrect email or password.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please try again later.';
    case 'auth/invalid-phone-number':
      return 'Invalid phone number. Include country code (e.g. +222...).';
    case 'auth/invalid-verification-code':
      return 'Incorrect OTP code. Please try again.';
    case 'auth/code-expired':
      return 'OTP expired. Please request a new one.';
    default:
      return 'Access restricted. Please sign in.';
  }
}
